import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Fab,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { useDispatch } from 'react-redux';
import GenericLoader from '../../../../components/Loader/GenericLoader';
import { DialogBoxComponent } from '../../../../components/DialogBox/DialogBoxCard';
import {
  LeavesTypesLoaderAttribute,
  SHOW_NOTIFICATION,
} from '../../../../constants';
import { getLeaveGroups } from '../../../Services/MasterdataService';
import LeaveGroupForm from './LeaveGroupForm';
import { formatBandSummary } from './bandConfigurationUtils';

const LeaveGroups = () => {
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(false);
  const [showError, setShowError] = useState(false);
  const [leaveGroups, setLeaveGroups] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formType, setFormType] = useState('create');
  const [selectedGroup, setSelectedGroup] = useState(null);

  const dispatchNotification = (typ, msg) => {
    dispatch({
      type: SHOW_NOTIFICATION,
      data: {
        open: true,
        type: typ,
        message: msg,
      },
    });
  };

  const loadData = async () => {
    setLoader(true);
    const response = await getLeaveGroups();
    if (response.isError === false) {
      setLeaveGroups(response.data?.data || []);
      setLoader(false);
    } else {
      setLoader(false);
      dispatchNotification(response.type, response.message);
    }
  };

  useEffect(() => {
    loadData();
  }, [refresh]);

  const handleCreate = () => {
    setFormType('create');
    setSelectedGroup(null);
    setFormOpen(true);
  };

  const handleEdit = (group) => {
    setFormType('update');
    setSelectedGroup(group);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedGroup(null);
  };

  return (
    <div>
      <Box className="main-content-overlay" sx={{ pt: '10px' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 1 }}>
          Manage Leave Groups that define band eligibility and per-band quarterly
          Casual Leave allocations. Assign groups to employees via onboarding or
          profile.
        </Typography>

        {loader ? (
          <GenericLoader
            rectAttributes={LeavesTypesLoaderAttribute}
            showError={showError}
            setShowError={setShowError}
          />
        ) : (
          <Paper sx={{ width: '100%', overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Group Name</TableCell>
                  <TableCell>Band quarterly allocations</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leaveGroups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No Leave Groups yet. Create one to configure quarterly
                      Casual Leave bands.
                    </TableCell>
                  </TableRow>
                ) : (
                  leaveGroups.map((group) => (
                    <TableRow key={group.id} hover>
                      <TableCell>{group.groupName}</TableCell>
                      <TableCell sx={{ maxWidth: 520 }}>
                        <Typography
                          variant="body2"
                          title={formatBandSummary(group.bandConfiguration)}
                        >
                          {formatBandSummary(group.bandConfiguration)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={group.isActive ? 'Active' : 'Inactive'}
                          color={group.isActive ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEdit(group)}
                          aria-label="Edit leave group"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        )}

        <Fab
          color="primary"
          style={{
            right: 40,
            bottom: 50,
            position: 'fixed',
            zIndex: 1051,
          }}
          onClick={handleCreate}
          aria-label="Create leave group"
        >
          <AddIcon />
        </Fab>

        <DialogBoxComponent
          open={formOpen}
          fullWidth
          maxWidth="lg"
          handleClose={handleFormClose}
          component={
            <LeaveGroupForm
              type={formType}
              data={selectedGroup}
              refresh={refresh}
              setRefresh={setRefresh}
              handleFormClose={handleFormClose}
            />
          }
        />
      </Box>
    </div>
  );
};

export default LeaveGroups;
